import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '../../layout/AppLayout.jsx'
import Card from '../../components/ui/Card.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Modal } from '../../components/ui/Modal.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import api from '../../lib/api.js'
import { formatCurrency, formatDate } from '../../lib/format.js'
import { downloadBlob } from '../../lib/download.js'
import { isValidVat } from '../../lib/validation.js'
import { invoiceStatus, peppolBadge, isOutgoing } from './invoiceStatus.js'

/*
 * Invoice detail — inventory §4. Read-only view of one invoice (outgoing or
 * incoming) with lifecycle actions: mark paid, edit/delete (drafts), download
 * PDF/UBL, and the Peppol delivery panel (retry/refresh). Server provides line
 * and invoice amounts; we group items by VAT rate for the breakdown.
 */
export default function InvoiceDetailPage() {
  const { id } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { confirm, toast, notify } = useFeedback()
  const { getCurrentCompany } = useCompany()

  const [invoice, setInvoice] = useState(null)
  const [partner, setPartner] = useState(null)
  const [sepaQr, setSepaQr] = useState(null)
  const [attachments, setAttachments] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [attView, setAttView] = useState(null) // { att, url } for the preview modal

  const openAttachment = async (att) => {
    try {
      const { data: blob } = await api.get(`/invoices/${id}/attachments/${att.id}`, { responseType: 'blob' })
      setAttView({ att, url: URL.createObjectURL(blob) })
    } catch { toast(t('invoiceDetail.downloadError'), 'error') }
  }
  const closeAttView = () => { if (attView?.url) URL.revokeObjectURL(attView.url); setAttView(null) }

  const loadAttachments = useCallback(() => {
    api.get(`/invoices/${id}/attachments`).then(({ data }) => setAttachments(data?.attachments || [])).catch(() => setAttachments([]))
  }, [id])

  const load = useCallback(() => {
    setLoading(true)
    setSepaQr(null)
    loadAttachments()
    api.get(`/invoices/${id}`)
      .then(async ({ data }) => {
        const inv = data?.invoice
        setInvoice(inv)
        if (inv?.customer_id) {
          const c = await api.get(`/customers/${inv.customer_id}`).catch(() => null)
          setPartner(c?.data?.customer || c?.data || null)
        } else if (inv?.supplier_id) {
          const s = await api.get(`/suppliers/${inv.supplier_id}`).catch(() => null)
          setPartner(s?.data?.supplier || s?.data || null)
          // SEPA payment QR — incoming, unpaid, finalized invoices only (to pay
          // the supplier). Optional: silently ignored if unavailable.
          if (!inv.is_draft && inv.status !== 'paid') {
            api.get(`/invoices/${id}/sepa-qr-code`).then((r) => setSepaQr(r.data)).catch(() => {})
          }
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [id, loadAttachments])

  useEffect(() => { load() }, [load])

  const downloadAttachment = (att) => downloadBlob(`/invoices/${id}/attachments/${att.id}`, att.filename).catch(() => toast(t('invoiceDetail.downloadError'), 'error'))
  const deleteAttachment = async (att) => {
    const ok = await confirm({ tone: 'danger', destructive: true, title: t('invoiceDetail.attachments.confirmDelete'), message: att.filename, confirmLabel: t('common.delete'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.delete(`/invoices/${id}/attachments/${att.id}`); toast(t('invoiceDetail.attachments.deleted'), 'success'); loadAttachments() }
    catch { toast(t('invoices.toast.error'), 'error') }
  }

  const markPaid = async () => {
    setBusy(true)
    try {
      await api.post(`/invoices/${id}/mark-as-paid`)
      toast(t('invoices.toast.markedPaid'), 'success')
      load()
    } catch { toast(t('invoices.toast.error'), 'error') } finally { setBusy(false) }
  }

  const removeDraft = async () => {
    const ok = await confirm({
      tone: 'danger', destructive: true,
      title: t('invoices.confirmDelete.title'),
      message: t('invoices.confirmDelete.message', { number: invoice.invoice_number || `#${id}` }),
      confirmLabel: t('invoices.confirmDelete.confirm'), cancelLabel: t('common.cancel'),
    })
    if (!ok) return
    try {
      await api.delete(`/invoices/${id}`)
      toast(t('invoices.toast.deleted'), 'success')
      navigate('/app/invoices')
    } catch { toast(t('invoices.toast.error'), 'error') }
  }

  const download = async (kind) => {
    setBusy(true)
    try {
      await downloadBlob(`/invoices/${id}/${kind}`, `${invoice.invoice_number || id}.${kind === 'pdf' ? 'pdf' : 'xml'}`)
    } catch { toast(t('invoiceDetail.downloadError'), 'error') } finally { setBusy(false) }
  }

  // Top "PDF" button: download the stored main PDF attachment when there is one
  // (the only PDF for an incoming invoice — /invoices/:id/pdf rejects those), and
  // only fall back to on-the-fly generation for outgoing drafts without an attachment.
  const downloadPdf = async () => {
    // Generated PDF flag (outgoing) first; otherwise the first PDF attachment —
    // incoming invoices store their PDF as a plain attachment (no is_invoice_pdf).
    const main = attachments.find((a) => a.is_invoice_pdf)
      || attachments.find((a) => (a.content_type || '').includes('pdf') || /\.pdf$/i.test(a.filename || ''))
    setBusy(true)
    try {
      if (main) await downloadBlob(`/invoices/${id}/attachments/${main.id}`, main.filename || `${invoice.invoice_number || id}.pdf`)
      else await downloadBlob(`/invoices/${id}/pdf`, `${invoice.invoice_number || id}.pdf`)
    } catch { toast(t('invoiceDetail.downloadError'), 'error') } finally { setBusy(false) }
  }

  const peppolAction = async (action) => {
    setBusy(true)
    try {
      const { data } = await api.post(`/invoices/${id}/${action}`)
      setInvoice((inv) => ({ ...inv, peppol_status: data?.peppol_status ?? inv.peppol_status, peppol_feedback: data?.peppol_feedback ?? inv.peppol_feedback }))
      toast(t('invoiceDetail.peppol.updated'), 'success')
    } catch { toast(t('invoices.toast.error'), 'error') } finally { setBusy(false) }
  }

  if (loading) return <Page title={t('invoiceDetail.title')}><Card>{t('common.loading')}</Card></Page>
  if (notFound || !invoice) {
    return (
      <Page title={t('invoiceDetail.title')}>
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ color: 'var(--color-text)', marginBottom: 16 }}>{t('invoiceDetail.notFound')}</div>
          <Button variant="secondary" icon="chevron-right" onClick={() => navigate('/app/invoices')} style={{ transform: 'none' }}>{t('invoiceDetail.backToList')}</Button>
        </Card>
      </Page>
    )
  }

  const out = isOutgoing(invoice)
  const st = invoiceStatus(invoice)
  const pp = peppolBadge(invoice)
  // Outgoing invoice whose recipient has a broken (BE) VAT — Peppol resend would fail.
  const partnerVatBroken = out && !!partner?.vat_number && !isValidVat(partner.vat_number)
  const sender = getCurrentCompany()
  const canMarkPaid = !invoice.is_draft && !invoice.is_credit_note && invoice.status !== 'paid' && invoice.status !== 'cancelled'

  // VAT breakdown grouped by rate from server-side line amounts.
  const byRate = new Map()
  for (const it of invoice.items || []) {
    const rate = it.vat_rate?.rate ?? 0
    const cur = byRate.get(rate) || { rate, base: 0, vat: 0 }
    cur.base += Number(it.amount_excluding_tax) || 0
    cur.vat += Number(it.tax_amount) || 0
    byRate.set(rate, cur)
  }
  // Only surface the per-rate breakdown when it reconciles with the server's
  // total VAT. Incoming Peppol/UBL invoices may carry document-level allowances
  // that the line sums don't capture — showing a mismatching line is misleading.
  const breakdownVat = [...byRate.values()].reduce((s, b) => s + b.vat, 0)
  const reconciles = Math.abs(breakdownVat - (Number(invoice.tax_amount) || 0)) < 0.02
  const vatBreakdown = reconciles ? [...byRate.values()].sort((a, b) => b.rate - a.rate) : []

  // Seed for duplicate / credit-note (prefills a NEW invoice via router state).
  const buildSeed = (asCreditNote) => ({
    customer_id: invoice.customer_id,
    is_credit_note: asCreditNote,
    items: (invoice.items || []).map((it) => ({
      description: it.description, quantity: it.quantity, unit_price: it.unit_price,
      vat_rate_id: it.vat_rate_id ?? it.vat_rate?.id, note: it.note,
      allowance_charge_amount: it.allowance_charge_amount, allowance_charge_reason: it.allowance_charge_reason,
      allowance_charge_is_percentage: it.allowance_charge_is_percentage, is_charge: it.is_charge,
    })),
    ...(asCreditNote ? { document_references: [{ reference_type: 'BILLING', reference_id: invoice.invoice_number }] } : {}),
  })
  const canCreditNote = out && !invoice.is_draft && !invoice.is_credit_note

  const action = (
    <div style={{ display: 'flex', gap: 10 }}>
      <Button variant="secondary" icon="invoice" onClick={() => navigate('/app/invoices')}>{t('invoiceDetail.backToList')}</Button>
      {canMarkPaid && <Button variant="success" icon="check" loading={busy} onClick={markPaid}>{t('invoices.actions.markPaid')}</Button>}
    </div>
  )

  return (
    <Page title={`${invoice.invoice_number || `#${id}`}`} action={action} maxWidth={1000}>
      {/* Action bar */}
      <Card style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Badge tone={out ? 'info' : 'neutral'}>{out ? t('invoices.direction.sent') : t('invoices.direction.received')}</Badge>
        <Badge tone={st.tone}>{t(st.key)}</Badge>
        {pp && <Badge tone={pp.tone} title={pp.title}>{pp.key ? t(pp.key) : pp.raw}</Badge>}
        <div style={{ flex: 1 }} />
        {/* PDF available for outgoing invoices, including drafts (preview). */}
        {(out || !invoice.is_draft) && <Button variant="secondary" icon="download" loading={busy} onClick={downloadPdf}>{invoice.is_draft ? t('invoiceDetail.pdfPreview') : 'PDF'}</Button>}
        {!invoice.is_draft && out && <Button variant="secondary" icon="download" loading={busy} onClick={() => download('ubl')}>UBL</Button>}
        {out && <Button variant="secondary" icon="copy" onClick={() => navigate('/app/invoices/new', { state: { seed: buildSeed(false) } })}>{t('invoiceDetail.duplicate')}</Button>}
        {canCreditNote && <Button variant="secondary" icon="note" onClick={() => navigate('/app/invoices/new', { state: { seed: buildSeed(true) } })}>{t('invoiceDetail.creditNote')}</Button>}
        {invoice.is_draft && <Button variant="secondary" icon="pencil" onClick={() => navigate(`/app/invoices/${id}/edit`)}>{t('common.edit')}</Button>}
        {invoice.is_draft && <Button variant="danger" icon="trash" onClick={removeDraft}>{t('common.delete')}</Button>}
      </Card>

      {/* Parties + dates */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
        <Card>
          <SectionTitle>{out ? t('invoiceDetail.from') : t('invoiceDetail.supplier')}</SectionTitle>
          <PartyBlock party={out ? sender : partner} />
        </Card>
        <Card>
          <SectionTitle>{out ? t('invoiceDetail.to') : t('invoiceDetail.recipient')}</SectionTitle>
          <PartyBlock party={out ? partner : sender} />
        </Card>
      </div>

      <Card style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <Info label={t('invoiceForm.issueDate')} value={formatDate(invoice.issue_date)} />
          {!invoice.is_credit_note && <Info label={t('invoiceForm.dueDate')} value={formatDate(invoice.due_date)} />}
          {invoice.payment_date && <Info label={t('invoiceDetail.paymentDate')} value={formatDate(invoice.payment_date)} />}
          {invoice.structured_communication && <Info label={t('invoiceDetail.structuredComm')} value={invoice.structured_communication} mono />}
        </div>
      </Card>

      {/* Lines */}
      <Card padding={0} style={{ marginBottom: 18, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-lilac)' }}>
                <th style={dTh('left')}>{t('invoiceForm.cols.description')}</th>
                <th style={dTh('right')}>{t('invoiceForm.cols.qty')}</th>
                <th style={dTh('right')}>{t('invoiceForm.cols.unitPrice')}</th>
                <th style={dTh('right')}>{t('invoiceForm.cols.vat')}</th>
                <th style={dTh('right')}>{t('invoiceDetail.lineHt')}</th>
                <th style={dTh('right')}>{t('invoiceForm.cols.total')}</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.items || []).map((it) => (
                <tr key={it.id} style={{ borderTop: '1px solid var(--color-border-row)' }}>
                  <td style={dTd}>
                    <div style={{ color: 'var(--color-ink)', fontWeight: 500 }}>{it.description}</div>
                    {it.note && <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginTop: 2 }}>{it.note}</div>}
                    {Number(it.allowance_charge_amount) > 0 && (
                      <div style={{ fontSize: 12.5, color: it.is_charge ? 'var(--color-info)' : 'var(--color-danger)', marginTop: 2 }}>
                        {it.is_charge ? '+' : '−'} {it.allowance_charge_is_percentage ? `${it.allowance_charge_amount}%` : formatCurrency(it.allowance_charge_amount)}
                        {it.allowance_charge_reason ? ` · ${it.allowance_charge_reason}` : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ ...dTd, textAlign: 'right' }}>{Number(it.quantity)}</td>
                  <td style={{ ...dTd, textAlign: 'right' }}>{formatCurrency(it.unit_price)}</td>
                  <td style={{ ...dTd, textAlign: 'right' }}>{it.vat_rate?.rate ?? 0}%</td>
                  <td style={{ ...dTd, textAlign: 'right' }}>{formatCurrency(it.amount_excluding_tax)}</td>
                  <td style={{ ...dTd, textAlign: 'right', fontFamily: 'var(--font-title)', fontWeight: 600 }}>{formatCurrency(it.amount_including_tax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Totals + payment */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <Card>
          <SectionTitle>{out ? t('invoiceDetail.paymentTo') : t('invoiceDetail.supplierBank')}</SectionTitle>
          <PaymentBlock invoice={invoice} out={out} t={t} />
          {sepaQr?.qr_code && (
            <>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 16, padding: 14, background: 'var(--color-bg-lilac)', borderRadius: 12 }}>
                <img src={sepaQr.qr_code} alt={t('invoiceDetail.sepaQr.alt')} width={104} height={104} style={{ flex: 'none', borderRadius: 8, background: '#fff' }} />
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--color-ink)', marginBottom: 4 }}>{t('invoiceDetail.sepaQr.title')}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--color-text)' }}>{t('invoiceDetail.sepaQr.hint')}</div>
                  {sepaQr.payment_info?.reference && <div style={{ fontFamily: 'monospace', fontSize: 12.5, color: 'var(--color-text-muted)', marginTop: 6 }}>{sepaQr.payment_info.reference}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginTop: 10, padding: '10px 12px', background: 'var(--color-warning-bg, #fff7e6)', border: '1px solid var(--color-warning, #e0a800)', borderRadius: 10, fontSize: 12.5, color: 'var(--color-text)' }}>
                <span style={{ color: 'var(--color-warning, #b8860b)', flexShrink: 0, marginTop: 1 }}><Icon name="alert-triangle" size={15} /></span>
                <span>{t('invoiceDetail.sepaQr.warning')}</span>
              </div>
            </>
          )}
          {invoice.notes && (
            <>
              <div style={{ height: 1, background: 'var(--color-border)', margin: '14px 0' }} />
              <Info label={t('invoiceForm.notes')} value={invoice.notes} />
            </>
          )}
        </Card>
        <Card>
          <SectionTitle>{t('invoiceForm.totals')}</SectionTitle>
          <DRow label={t('invoiceForm.subtotal')} value={formatCurrency(invoice.amount_excluding_tax)} />
          {vatBreakdown.filter((b) => b.vat > 0).map((b) => (
            <DRow key={b.rate} label={`${t('invoiceForm.vat')} ${b.rate}%`} value={formatCurrency(b.vat)} muted />
          ))}
          <DRow label={t('invoiceForm.totalVat')} value={formatCurrency(invoice.tax_amount)} />
          <div style={{ height: 1, background: 'var(--color-border)', margin: '12px 0' }} />
          <DRow label={t('invoiceForm.totalTtc')} value={formatCurrency(invoice.amount_including_tax)} big />
        </Card>
      </div>

      {/* Peppol delivery panel (outgoing) */}
      {out && invoice.peppol_status && (
        <Card style={{ marginTop: 18 }}>
          <SectionTitle>{t('invoiceDetail.peppol.title')}</SectionTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {pp && <Badge tone={pp.tone}>{pp.key ? t(pp.key) : pp.raw}</Badge>}
            {invoice.peppol_feedback && <span style={{ color: 'var(--color-text)', fontSize: 13 }}>{invoice.peppol_feedback}</span>}
            <div style={{ flex: 1 }} />
            {['ABANDONED', 'ERROR'].includes(invoice.peppol_status)
              ? <Button variant="secondary" icon="send" loading={busy} disabled={partnerVatBroken} onClick={() => peppolAction('retry-peppol')}>{t('invoiceDetail.peppol.retry')}</Button>
              : invoice.peppol_status !== 'OK' && <Button variant="secondary" icon="settings" loading={busy} onClick={() => peppolAction('refresh-peppol-status')}>{t('invoiceDetail.peppol.refresh')}</Button>}
          </div>
          {/* Failed send + invalid recipient VAT: explain the cause and route the
              user to the customer sheet — retrying as-is is doomed. */}
          {['ABANDONED', 'ERROR'].includes(invoice.peppol_status) && partnerVatBroken && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, padding: '10px 14px', background: 'var(--color-danger-bg)', border: '1px solid var(--color-danger)', borderRadius: 10, flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--color-danger)', flexShrink: 0 }}><Icon name="alert-triangle" size={16} /></span>
              <span style={{ flex: 1, fontSize: 13, color: 'var(--color-text)', minWidth: 220 }}>{t('invoiceDetail.peppol.vatBroken', { vat: partner?.vat_number })}</span>
              <Button variant="danger" icon="pencil" onClick={() => navigate(`/app/customers/${invoice.customer_id}`)}>{t('invoiceDetail.peppol.fixCustomer')}</Button>
            </div>
          )}
        </Card>
      )}

      {/* Attachments */}
      {attachments.length > 0 && (
        <Card style={{ marginTop: 18 }}>
          <SectionTitle>{t('invoiceDetail.attachments.title')}</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {attachments.map((att) => (
              <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 10 }}>
                <span style={{ color: 'var(--color-primary)' }}><Icon name={att.is_ubl_xml ? 'link' : 'invoice'} size={18} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: 'var(--color-ink)', fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{att.filename}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{Math.round((att.file_size || 0) / 1024)} Ko{att.is_invoice_pdf ? ' · PDF' : att.is_ubl_xml ? ' · UBL' : ''}</div>
                </div>
                {(att.is_invoice_pdf || /\.pdf$/i.test(att.filename || '') || (att.mime_type || '').includes('pdf')) && (
                  <IconButton icon="eye" variant="primary" title={t('common.preview')} onClick={() => openAttachment(att)} />
                )}
                <Button variant="secondary" icon="download" onClick={() => downloadAttachment(att)}>{t('common.download')}</Button>
                {invoice.is_draft && !att.is_invoice_pdf && <IconButton icon="trash" variant="danger" title={t('common.delete')} onClick={() => deleteAttachment(att)} />}
              </div>
            ))}
          </div>
        </Card>
      )}

      {attView && (
        <Modal open onClose={closeAttView} maxWidth={920}>
          <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '88vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--color-border-row)' }}>
              <Icon name="invoice" size={18} />
              <div style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, color: 'var(--color-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{attView.att.filename}</div>
              <Button variant="secondary" icon="download" onClick={() => downloadAttachment(attView.att)}>{t('common.download')}</Button>
              <Button variant="secondary" icon="x" onClick={closeAttView}>{t('common.close')}</Button>
            </div>
            <iframe title={attView.att.filename} src={attView.url} style={{ width: '100%', height: '74vh', border: 'none', display: 'block', background: 'var(--color-bg-page)' }} />
          </div>
        </Modal>
      )}
    </Page>
  )
}

/* ---- helpers ---- */
const dTh = (align) => ({ textAlign: align, padding: '11px 16px', fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' })
const dTd = { padding: '12px 16px', color: 'var(--color-text)', verticalAlign: 'top' }

function SectionTitle({ children }) {
  return <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 14, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 12 }}>{children}</h3>
}

function PartyBlock({ party }) {
  if (!party) return <div style={{ color: 'var(--color-text-muted)' }}>—</div>
  return (
    <div style={{ lineHeight: 1.6 }}>
      <div style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16, color: 'var(--color-ink)' }}>{party.name}</div>
      {party.vat_number && <div style={{ fontSize: 13, color: 'var(--color-text)' }}>{party.vat_number}</div>}
      {party.address && <div style={{ fontSize: 13, color: 'var(--color-text)' }}>{party.address}</div>}
      {(party.postal_code || party.city) && <div style={{ fontSize: 13, color: 'var(--color-text)' }}>{party.postal_code} {party.city} {party.country ? `· ${party.country}` : ''}</div>}
      {party.email && <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{party.email}</div>}
    </div>
  )
}

function PaymentBlock({ invoice, out, t }) {
  if (invoice.payment_method === 'card' && invoice.payment_card_last_digits) {
    return (
      <div style={{ lineHeight: 1.7 }}>
        <Info label={t('invoiceForm.card')} value={`•••• ${invoice.payment_card_last_digits}`} />
        {invoice.payment_card_holder_name && <Info label={t('invoiceDetail.cardHolder')} value={invoice.payment_card_holder_name} />}
      </div>
    )
  }
  const acc = out ? invoice.bank_account : invoice.supplier_bank_account
  if (!acc) return <div style={{ color: 'var(--color-text-muted)' }}>—</div>
  return (
    <div style={{ lineHeight: 1.7 }}>
      {acc.account_name && <div style={{ fontWeight: 600, color: 'var(--color-ink)' }}>{acc.account_name}</div>}
      {acc.iban && <div style={{ fontFamily: 'monospace', fontSize: 13.5, color: 'var(--color-ink)' }}>{acc.iban}</div>}
      {acc.bic && <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{acc.bic}{acc.bank_name ? ` · ${acc.bank_name}` : ''}</div>}
    </div>
  )
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--color-ink)', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</div>
    </div>
  )
}

function DRow({ label, value, big, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '3px 0' }}>
      <span style={{ fontSize: big ? 15 : 13.5, color: muted ? 'var(--color-text-muted)' : 'var(--color-text)', fontWeight: big ? 700 : 500 }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-title)', fontSize: big ? 22 : 14, fontWeight: big ? 800 : 600, color: big ? 'var(--color-success)' : 'var(--color-ink)' }}>{value}</span>
    </div>
  )
}
